import * as React from "react";
import { graphql, useFragment } from "react-relay";
import { useReviewToolUseMutation } from "src/pages/AskPage/hooks/useReviewToolUseMutation";

import { AgentThoughtProcess_formattedMessage$key } from "./__generated__/AgentThoughtProcess_formattedMessage.graphql";
import { AgentThoughtProcess_llmConversation$key } from "./__generated__/AgentThoughtProcess_llmConversation.graphql";
import { AgentThoughtProcess_message$key } from "./__generated__/AgentThoughtProcess_message.graphql";
import { AgentPendingQuestionType } from "./AgentQuestion";
import { ToolCallSheet } from "./tool-calls/ToolCallSheet";
import { V2AgentThoughtProcessRenderer } from "./tool-calls/V2AgentThoughtProcessRenderer";
import { isFilesystemTool } from "./tool-calls/v2Utils";

interface AgentThoughtProcessProps {
  message: AgentThoughtProcess_message$key | AgentThoughtProcess_formattedMessage$key;
  conversation: AgentThoughtProcess_llmConversation$key;
  /** Whether this interaction has a timed out question - affects interrupt message display */
  hasTimedOutQuestion?: boolean;
  /** Pending questions for this interaction */
  pendingQuestions?: AgentPendingQuestionType[];
  /** Handler for submitting question answers */
  onQuestionSubmit?: (toolUseId: string, answers: any[]) => void;
  /** Whether a question answer is being submitted */
  isSubmittingQuestion?: boolean;
}

const llmConversationFragment = graphql`
  fragment AgentThoughtProcess_llmConversation on GraphQLLlmConversation {
    id
    threadState
    pendingToolUses {
      id
      toolUseId
      status
      llmConversationId
      llmInteractionId
      conversationId
    }
  }
`;

const messageFragment = graphql`
  fragment AgentThoughtProcess_message on GraphQLFormattedMessage {
    id
    messageType
    startedAt
    endedAt
    agentThoughtProcess {
      text
      thinking
      thinkingSummary
      subagentDescription
      subagentOutput
      subagentItems {
        text
        thinking
        thinkingSummary
        toolUse {
          toolUse {
            id
            name
          }
          ...V2AgentThoughtProcessRenderer_toolCall
          ...ToolCallSheet_toolCall
          ...ChecklistToolCallResult_toolCall
          ...SkillCluster_toolCall
        }
      }
      toolUse {
        toolUse {
          id
          name
        }
        ...V2AgentThoughtProcessRenderer_toolCall
        ...ToolCallSheet_toolCall
        ...ChecklistToolCallResult_toolCall
        ...SkillCluster_toolCall
      }
    }
  }
`;

const formattedMessageFragment = graphql`
  fragment AgentThoughtProcess_formattedMessage on GraphQLFormattedMessage {
    id
    messageType
    startedAt
    endedAt
    agentThoughtProcess {
      text
      thinking
      thinkingSummary
      subagentDescription
      subagentOutput
      subagentItems {
        text
        thinking
        thinkingSummary
        toolUse {
          toolUse {
            id
            name
          }
          ...V2AgentThoughtProcessRenderer_toolCall
          ...ToolCallSheet_toolCall
          ...ChecklistToolCallResult_toolCall
          ...SkillCluster_toolCall
        }
      }
      toolUse {
        toolUse {
          id
          name
        }
        ...V2AgentThoughtProcessRenderer_toolCall
        ...ToolCallSheet_toolCall
        ...ChecklistToolCallResult_toolCall
        ...SkillCluster_toolCall
      }
    }
  }
`;

export const AgentThoughtProcess: React.FC<AgentThoughtProcessProps> = ({
  message: messageKey,
  conversation: conversationKey,
  hasTimedOutQuestion = false,
  pendingQuestions = [],
  onQuestionSubmit,
  isSubmittingQuestion = false,
}) => {
  const conversation = useFragment(llmConversationFragment, conversationKey);

  // Check which message fragment type to use
  const isFormattedMessage = (messageKey as any)?.__fragments?.AgentThoughtProcess_formattedMessage !== undefined;

  const messageData = useFragment(
    isFormattedMessage ? formattedMessageFragment : messageFragment,
    messageKey as any
  );
  const { agentThoughtProcess, messageType, startedAt, endedAt } = messageData;

  const isConversationInProgress = conversation.threadState === "PENDING";
  const isThinking = isConversationInProgress && messageType === "AGENT_PROCESS";

  // State for tool sheet
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);
  const [activeToolIndex, setActiveToolIndex] = React.useState(0);

  // Tool approval mutation
  const { reviewToolUse, isInFlight } = useReviewToolUseMutation();

  // Track which tool use IDs are waiting for UI update after approval
  const [approvalsPendingUpdate, setApprovalsPendingUpdate] = React.useState<Set<string>>(new Set());

  // Defensive: ensure pendingToolUses is always an array
  const safePendingToolUses = conversation.pendingToolUses || [];

  // Clear tool use IDs from pending set when they're no longer in pendingToolUses (UI has updated)
  React.useEffect(() => {
    const currentPendingIds = new Set(safePendingToolUses.map(p => p.toolUseId));
    setApprovalsPendingUpdate(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const id of next) {
        if (!currentPendingIds.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [safePendingToolUses]);

  // Approval handlers
  const handleApproveToolUse = React.useCallback(
    async (toolUseId: string) => {
      try {
        // Find the pending tool use to get llmConversationId and llmInteractionId
        const pendingToolUse = safePendingToolUses.find(
          (p) => p.toolUseId === toolUseId
        );

        // Mark as pending update (will stay loading until UI updates)
        setApprovalsPendingUpdate(prev => new Set(prev).add(toolUseId));

        await reviewToolUse(
          toolUseId,
          "APPROVED",
          pendingToolUse?.conversationId,
          pendingToolUse?.llmConversationId,
          pendingToolUse?.llmInteractionId
        );
      } catch (error) {
        console.error("Failed to approve tool use:", error);
        // Remove from pending on error
        setApprovalsPendingUpdate(prev => {
          const next = new Set(prev);
          next.delete(toolUseId);
          return next;
        });
      }
    },
    [reviewToolUse, safePendingToolUses]
  );

  const handleRejectToolUse = React.useCallback(
    async (toolUseId: string) => {
      try {
        // Find the pending tool use to get llmConversationId and llmInteractionId
        const pendingToolUse = safePendingToolUses.find(
          (p) => p.toolUseId === toolUseId
        );

        // Mark as pending update (will stay loading until UI updates)
        setApprovalsPendingUpdate(prev => new Set(prev).add(toolUseId));

        await reviewToolUse(
          toolUseId,
          "REJECTED",
          pendingToolUse?.conversationId,
          pendingToolUse?.llmConversationId,
          pendingToolUse?.llmInteractionId
        );
      } catch (error) {
        console.error("Failed to reject tool use:", error);
        // Remove from pending on error
        setApprovalsPendingUpdate(prev => {
          const next = new Set(prev);
          next.delete(toolUseId);
          return next;
        });
      }
    },
    [reviewToolUse, safePendingToolUses]
  );

  // Combined loading state: either mutation in flight OR waiting for UI update
  const isApprovalLoading = isInFlight || approvalsPendingUpdate.size > 0;

  // Get all clickable tool uses from agentThoughtProcess (exclude filesystem tools and UpdateTodoTool)
  const toolUses = React.useMemo(() => {
    return (agentThoughtProcess || [])
      .map((item: any, index: number) => ({ toolUse: item.toolUse, originalIndex: index }))
      .filter((item: any) => {
        if (!item.toolUse) return false;
        const toolName = item.toolUse.toolUse?.name;
        if (!toolName) return false;
        if (toolName === "UpdateTodoTool") return false;
        return !isFilesystemTool(toolName);
      });
  }, [agentThoughtProcess]);

  // Render with empty state if no items but still in progress
  const hasItems = agentThoughtProcess && agentThoughtProcess.length > 0;

  // Don't render at all if not thinking and no items
  if (!isThinking && !hasItems) {
    return null;
  }

  const handleToolClick = React.useCallback((index: number) => {
    // Find the tool use at this index
    const toolIndex = toolUses.findIndex((item: any) => item.originalIndex === index);
    if (toolIndex !== -1) {
      setActiveToolIndex(toolIndex);
      setIsSheetOpen(true);
    }
  }, [toolUses]);

  const moveToNextTool = React.useCallback(() => {
    if (activeToolIndex < toolUses.length - 1) {
      setActiveToolIndex(activeToolIndex + 1);
    }
  }, [activeToolIndex, toolUses.length]);

  const moveToPrevTool = React.useCallback(() => {
    if (activeToolIndex > 0) {
      setActiveToolIndex(activeToolIndex - 1);
    }
  }, [activeToolIndex]);

  const activeTool = toolUses[activeToolIndex]?.toolUse;

  return (
    <>
      <div className="mb-3">
        <V2AgentThoughtProcessRenderer
          items={agentThoughtProcess || []}
          isThinking={isThinking}
          startedAt={startedAt}
          endedAt={endedAt}
          onToolClick={handleToolClick}
          pendingToolUses={safePendingToolUses}
          onApproveToolUse={handleApproveToolUse}
          onRejectToolUse={handleRejectToolUse}
          approvalInFlight={isApprovalLoading}
          hasTimedOutQuestion={hasTimedOutQuestion}
          pendingQuestions={pendingQuestions}
          onQuestionSubmit={onQuestionSubmit}
          isSubmittingQuestion={isSubmittingQuestion}
        />
      </div>

      {/* Tool sheet */}
      {isSheetOpen && activeTool && (
        <ToolCallSheet
          toolCall={activeTool}
          index={activeToolIndex}
          totalCount={toolUses.length}
          isOpen={isSheetOpen}
          onOpenChange={setIsSheetOpen}
          onNext={moveToNextTool}
          onPrevious={moveToPrevTool}
        />
      )}
    </>
  );
};